import type {
  AdvancedTriggerSettingsType,
  StatusIncludesConditionType,
} from '@cims-online/shared'
import type { LabeledValueType } from 'components/CIMSSelect/types'
import type { NotificationField, NotificationStatus } from '../../../types'

export type NotificationReferenceData = {
  fields: NotificationField[]
  statuses: NotificationStatus[]
}

type NotificationStatusReference =
  | Exclude<AdvancedTriggerSettingsType['from'], 'any_valid'>[number]
  | StatusIncludesConditionType['statuses'][number]

const SYSTEM_STATE_METADATA = {
  closed: { label: 'Closed' },
  create: {
    label: 'No previous status',
    tooltip:
      'The record is advancing for the first time and has no prior status',
  },
  draft: { label: 'Draft' },
} as const

/**
 * Creates a labeled reference for a notification system state.
 *
 * Only "No previous status" needs explanatory text. Draft and Closed are
 * self-explanatory and intentionally omit tooltips.
 */
export function createNotificationSystemStateReference(
  value: keyof typeof SYSTEM_STATE_METADATA
): LabeledValueType {
  return { value, ...SYSTEM_STATE_METADATA[value] }
}

/**
 * Creates a labeled reference for a custom workflow status.
 */
export function createNotificationCustomStatusReference(
  status: NotificationStatus
): LabeledValueType {
  return {
    deleted: status.deleted,
    label: status.name,
    tooltip: status.tooltip,
    value: `status:${status.id}`,
  }
}

/**
 * Converts labeled notification status values to their persisted references.
 *
 * Returns `undefined` when any value is not a supported system state or a
 * non-empty custom status reference.
 */
export function toNotificationStatusReferences(
  values: LabeledValueType[] | undefined
): NotificationStatusReference[] | undefined {
  const references: NotificationStatusReference[] = []

  for (const value of labeledValueStrings(values)) {
    if (value === 'create' || value === 'draft' || value === 'closed') {
      references.push({ type: value })
    } else if (value.startsWith('status:') && value.length > 'status:'.length) {
      references.push({
        type: 'status',
        statusId: value.slice('status:'.length),
      })
    } else {
      return undefined
    }
  }

  return references
}

function unknownReference(
  type: 'field' | 'option' | 'status',
  value: string
): LabeledValueType {
  return {
    deleted: true,
    label: `Unknown ${type} (${value})`,
    tooltip: `This ${type} could not be found.`,
    value,
  }
}

/**
 * Creates labeled notification-reference values from workflow metadata.
 *
 * Missing references remain visible as inactive values so administrators can
 * remove invalid persisted configuration without losing its identifier.
 */
export function createNotificationReferenceValues({
  fields,
  statuses,
}: NotificationReferenceData) {
  const fieldsById = new Map(fields.map((field) => [field.id, field]))
  const statusesById = new Map(statuses.map((status) => [status.id, status]))

  return {
    field(fieldId: string): LabeledValueType {
      const field = fieldsById.get(fieldId)
      return field
        ? {
            deleted: field.deleted,
            label: field.name,
            tooltip: field.tooltip,
            value: field.id,
          }
        : unknownReference('field', fieldId)
    },
    option(fieldId: string, optionId: string): LabeledValueType {
      const field = fieldsById.get(fieldId)
      const option =
        field?.settings.type === 'approvals'
          ? field.settings.options.find(({ id }) => id === optionId)
          : undefined

      return option
        ? {
            deleted: !!field?.deleted || !!option.deleted,
            label: option.label,
            tooltip: option.tooltip,
            value: option.id,
          }
        : unknownReference('option', optionId)
    },
    state(reference: NotificationStatusReference): LabeledValueType {
      if (reference.type !== 'status') {
        return createNotificationSystemStateReference(reference.type)
      }

      const status = statusesById.get(reference.statusId)
      return status
        ? createNotificationCustomStatusReference(status)
        : unknownReference('status', `status:${reference.statusId}`)
    },
  }
}

/**
 * Returns persisted string identifiers from labeled form values.
 */
export function labeledValueStrings(
  values: LabeledValueType[] | undefined
): string[] {
  return (values ?? []).map(({ value }) => value.toString())
}

/**
 * Returns one persisted string identifier from a labeled form value.
 */
export function labeledValueString(
  value: LabeledValueType | undefined
): string | undefined {
  return value?.value.toString()
}
